import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import {
  bulkUpsertPreferences,
  fetchAllNotifications,
  fetchChannelConfigs,
  fetchConfigurations,
  fetchInbox,
  fetchPreferences,
  fetchUnreadCount,
  markAllInboxRead,
  markNotificationRead,
  seedPreferences,
  testChannelConfig,
  upsertChannelConfig,
  upsertPreference,
} from '~/features/notifications/services/notifications.service';
import {
  isEmailEnabled,
  isInAppEnabled,
  type Audience,
  type ChannelTestPayload,
  type ChannelType,
  type EntityStatus,
  type PreferenceTarget,
  type UpsertChannelConfigPayload,
  type UpsertPreferencePayload,
} from '~/features/notifications/types/notifications.api.types';

/** Lists every channel-config row for the tenant. */
export function useChannelConfigs() {
  return useQuery({
    queryKey: queryKeys.notifications.channelConfigs(),
    queryFn: fetchChannelConfigs,
  });
}

/** Upserts a single channel's credentials + public config; refreshes the list on success. */
export function useUpsertChannelConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelType,
      payload,
    }: {
      channelType: ChannelType;
      payload: UpsertChannelConfigPayload;
    }) => upsertChannelConfig(channelType, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.channelConfigs() });
    },
  });
}

/** Fires a test message via the configured provider without creating a notification record. */
export function useTestChannel() {
  return useMutation({
    mutationFn: ({
      channelType,
      payload,
    }: {
      channelType: ChannelType;
      payload: ChannelTestPayload;
    }) => testChannelConfig(channelType, payload),
    onSuccess: (data) => {
      if (data.ok) toast.success('Test notification sent');
      else toast.error(data.error ?? 'Test notification failed');
    },
    onError: () => {
      toast.error('Test notification failed');
    },
  });
}

/**
 * Lists alert-topic configurations grouped under category_group.
 *
 * Pass an `audience` to scope to customer-facing or internal/ops topics; omit
 * for all. Cache key includes the audience so each scope is fetched/stored
 * separately.
 */
export function useConfigurations(audience?: Audience) {
  return useQuery({
    queryKey: queryKeys.notifications.configurations(audience),
    queryFn: () => fetchConfigurations(audience ? { audience } : {}),
  });
}

/**
 * Returns `true` when the email channel has at least one ACTIVE preference row,
 * `false` when confirmed off, and `undefined` while preferences are loading.
 * Use this to gate "We'll notify you via email" banners.
 */
export function useIsEmailEnabled(): boolean | undefined {
  const { data: preferences } = usePreferences();
  if (!preferences) return undefined;
  return isEmailEnabled(preferences);
}

/**
 * Same contract as {@link useIsEmailEnabled} but for the in-app channel. Use
 * alongside it to gate multi-channel "We'll notify you" banners.
 */
export function useIsInAppEnabled(): boolean | undefined {
  const { data: preferences } = usePreferences();
  if (!preferences) return undefined;
  return isInAppEnabled(preferences);
}

/** Lists current user's preference rows (toggle state per configuration × channel). */
export function usePreferences() {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: fetchPreferences,
  });
}

/**
 * Seeds default-disabled preferences on first use or after reset.
 *
 * The backend endpoint is documented as idempotent but the current
 * `seed_for_subsidiary` implementation is missing the per-row IntegrityError
 * catch that `_fanout_to_subsidiaries` has — re-seeding with existing rows
 * trips the `(subsidiary_id, configuration_id, channel_type)` unique
 * constraint and returns a generic 500. Until the backend ships the fix,
 * we treat that 500 as "already seeded" so the UX stays clean: refresh the
 * preferences list (no real data is lost) and surface a friendlier message.
 */
export function useSeedPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: seedPreferences,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
    },
    onError: () => {
      // Refetch in case some rows did seed before the conflict; UI ends up consistent.
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
    },
  });
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

/**
 * Fetches one page of the user's in-app notification inbox via
 * POST /notifications/inbox/list. Keyed by `{ page, size }` so each page caches
 * independently; `keepPreviousData` keeps the current page on screen (no empty
 * flash) while the next one loads.
 */
export function useInbox(page = 1, size = 20) {
  return useQuery({
    queryKey: queryKeys.notifications.inbox({ page, size }),
    queryFn: () => fetchInbox(page, size),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Single poll cadence (3 min) for the unread-count badge.
 *
 * The number is the *only* periodic notification request we issue: bell
 * badge, copilot shell, and inbox page all share this query via TanStack
 * Query's cache, so concurrent subscribers cost one network call. We
 * deliberately avoid refetching on focus/reconnect/background-interval to
 * keep the request shape predictable and prevent the burst that used to
 * happen when SSE replay events invalidated this query on every reconnect.
 */
const UNREAD_COUNT_POLL_INTERVAL_MS = 180_000;

/**
 * GET /notifications/inbox/unread-count — bell badge counter, polled every 180 s.
 *
 * Why polling (and not SSE-driven invalidation): the SSE stream replays
 * historical events on every reconnect; invalidating this query per replay
 * event flooded the notifications service with parallel requests and hung the
 * app. The poll resets on full page reload (fresh query mount = fresh timer)
 * and inbox auto-refresh is driven off count *deltas* in {@link useInboxAutoRefresh}.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: fetchUnreadCount,
    refetchInterval: UNREAD_COUNT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: UNREAD_COUNT_POLL_INTERVAL_MS,
  });
}

/**
 * Watches the cached unread count and invalidates the inbox + all-notifications
 * queries only when the count *increases* — so the list endpoints are not
 * called on every poll, only when there's something new to fetch.
 *
 * Must be mounted exactly once (in {@link NotificationsProvider}) to avoid
 * duplicate invalidations from multiple subscribers tracking their own prev.
 */
export function useInboxAutoRefresh(): void {
  const queryClient = useQueryClient();
  const { data: currentCount } = useUnreadCount();
  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (currentCount == null) return;
    const prev = prevCountRef.current;
    prevCountRef.current = currentCount;
    // Only refresh lists when new notifications arrive. A decrease (user
    // marked items read) is already handled by the mutation's own invalidate.
    if (prev != null && currentCount > prev) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.allNotifications() });
    }
  }, [currentCount, queryClient]);
}

/** Marks all inbox notifications as read; invalidates inbox + unread-count caches. */
export function useMarkAllInboxRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllInboxRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
    },
    onError: () => {
      toast.error('Could not mark notifications as read. Please try again.');
    },
  });
}

/** Marks a single notification as read; invalidates inbox + unread-count caches. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
    },
    onError: () => {
      // Refetch to reconcile read state in case the mutation partially applied.
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
    },
  });
}

/** Fetches all notifications for the full-page inbox view. */
export function useAllNotifications(page = 1, size = 50) {
  return useQuery({
    queryKey: queryKeys.notifications.allNotifications(),
    queryFn: () => fetchAllNotifications(page, size),
    staleTime: 30_000,
  });
}

/**
 * Bulk-upserts a preference for every `configuration_id` in `configurationIds`
 * via a single `PUT /preferences/bulk` request. Used by the Delivery Channels
 * screen to propagate a channel-level target (e.g. email recipients, in-app
 * auto-read days) and status across all alert-topic preferences for the active
 * subsidiary in one atomic round-trip instead of N parallel calls.
 */
export function useUpsertAllChannelPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelType,
      target,
      status,
      configurationIds,
    }: {
      channelType: ChannelType;
      target: PreferenceTarget;
      status: EntityStatus;
      configurationIds: string[];
    }) =>
      bulkUpsertPreferences({
        channel_type: channelType,
        configuration_ids: configurationIds,
        target,
        status,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
    },
  });
}

/** Upserts a preference; optimistic to keep alert-settings toggles snappy. */
export function useUpsertPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertPreferencePayload) => upsertPreference(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
    },
    onError: () => {
      toast.error('Could not save preference. Please try again.');
    },
  });
}
